import { decodeAudioDataCompat } from "./browserCompatibility";
import { SITE_TUNING } from "./siteTuning";

type VaultBreakersSound =
    | "hostVaultSelected"
    | "finalVault"
    | "dailyDouble"
    | "diamondFound"
    | "stashFound"
    | "vaultCracked"
    | "failedToCrack"
    | "wordSelected"
    | "vaultRunTheme"
    | "jeopardyTheme";

export type VaultBreakersQuestionTheme = "default" | "massiveLoot";

const SOUND_PATHS: Record<VaultBreakersSound, string> = {
    hostVaultSelected: "/audio/vault-breakers/host-vault-selected.mp3",
    finalVault: "/audio/vault-breakers/final-vault.mp3",
    dailyDouble: "/audio/vault-breakers/daily-double.mp3",
    diamondFound: "/audio/vault-breakers/diamond-found.mp3",
    stashFound: "/audio/vault-breakers/stash-found.mp3",
    vaultCracked: "/audio/vault-breakers/vault-cracked.mp3",
    failedToCrack: "/audio/vault-breakers/failed-to-crack.mp3",
    wordSelected: "/audio/vault-breakers/word-selected.mp3",
    vaultRunTheme: "/audio/vault-breakers/vault-run.mp3",
    jeopardyTheme: "/audio/vault-breakers/jeopardy-theme.mp3"
};

const SOUND_VOLUME: Record<VaultBreakersSound, number> = {
    hostVaultSelected: SITE_TUNING.vaultBreakers.audio.hostVaultSelectedVolume,
    finalVault: SITE_TUNING.vaultBreakers.audio.finalVaultVolume,
    dailyDouble: SITE_TUNING.vaultBreakers.audio.dailyDoubleVolume,
    diamondFound: SITE_TUNING.vaultBreakers.audio.diamondFoundVolume,
    stashFound: SITE_TUNING.vaultBreakers.audio.stashFoundVolume,
    vaultCracked: SITE_TUNING.vaultBreakers.audio.vaultCrackedVolume,
    failedToCrack: SITE_TUNING.vaultBreakers.audio.failedToCrackVolume,
    wordSelected: SITE_TUNING.vaultBreakers.audio.wordSelectedVolume,
    vaultRunTheme: SITE_TUNING.vaultBreakers.audio.vaultRunThemeVolume,
    jeopardyTheme: SITE_TUNING.vaultBreakers.audio.jeopardyThemeVolume
};

const QUESTION_THEME_SOUND: Record<VaultBreakersQuestionTheme, VaultBreakersSound> = {
    default: "vaultRunTheme",
    massiveLoot: "jeopardyTheme"
};

const QUESTION_THEME_START_OFFSET_SECONDS: Record<VaultBreakersQuestionTheme, number> = {
    default: 0,
    massiveLoot: SITE_TUNING.vaultBreakers.audio.jeopardyThemeStartOffsetSeconds
};

const QUESTION_THEME_PLAYBACK_RATE: Record<VaultBreakersQuestionTheme, number> = {
    default: 1,
    massiveLoot: SITE_TUNING.vaultBreakers.audio.jeopardyThemePlaybackRate
};

const GLOBAL_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.vaultBreakers.audio.globalVolumeMultiplier;
const MOBILE_SOUND_VOLUME_MULTIPLIER = SITE_TUNING.vaultBreakers.audio.mobileVolumeMultiplier;
const MOBILE_SOUND_SHORT_SIDE_MAX_PX = SITE_TUNING.vaultBreakers.audio.mobileVolumeShortSideMaxPx;

type ActiveQuestionTheme = {
    source: AudioBufferSourceNode;
    gain: GainNode;
    token: number;
    theme: VaultBreakersQuestionTheme;
};

export class VaultBreakersAudio {
    private context: AudioContext | null = null;
    private readonly buffers = new Map<VaultBreakersSound, AudioBuffer>();
    private readonly loading = new Map<VaultBreakersSound, Promise<void>>();
    private disposed = false;
    private primed = false;
    private contextToken = 0;
    private activeQuestionTheme: ActiveQuestionTheme | null = null;
    private questionThemeToken = 0;

    public async prepare(): Promise<void> {
        const context = await this.ensureContextReady();

        if (!context) {
            return;
        }

        await Promise.all(Object.keys(SOUND_PATHS).map((sound) => this.load(sound as VaultBreakersSound)));
    }

    public playHostVaultSelected() {
        this.play("hostVaultSelected");
    }

    public playFinalVault() {
        this.play("finalVault");
    }

    public playChooserSelected() {
        this.play("hostVaultSelected");
    }

    public playDailyDouble() {
        this.play("dailyDouble");
    }

    public playDiamondFound() {
        this.play("diamondFound");
    }

    public playStashFound() {
        this.play("stashFound");
    }

    public playVaultCracked() {
        this.play("vaultCracked");
    }

    public playFailedToCrack() {
        this.play("failedToCrack");
    }

    public playWordSelected() {
        this.play("wordSelected");
    }

    public playQuestionTheme(theme: VaultBreakersQuestionTheme = "default") {
        const token = this.questionThemeToken + 1;

        this.questionThemeToken = token;
        void this.playQuestionThemeWhenReady(token, theme);
    }

    public isQuestionThemeActive(theme: VaultBreakersQuestionTheme = "default"): boolean {
        return this.activeQuestionTheme?.theme === theme;
    }

    public fadeOutQuestionTheme() {
        this.stopQuestionTheme(SITE_TUNING.vaultBreakers.audio.finalLockFadeMs, { cancelPending: true });
    }

    public dispose() {
        this.disposed = true;
        this.stopQuestionTheme(0, { cancelPending: true });
        const context = this.context;
        this.resetContext(context, { clearBuffers: true });
        context?.close().catch(() => undefined);
    }

    private getContext(): AudioContext | null {
        if (this.disposed) {
            return null;
        }

        if (this.context?.state === "closed") {
            this.resetContext(this.context);
        }

        if (this.context) {
            return this.context;
        }

        const AudioContextConstructor =
            window.AudioContext ??
            (window as Window & typeof globalThis & { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;

        if (!AudioContextConstructor) {
            return null;
        }

        const context = new AudioContextConstructor();
        this.context = context;
        this.contextToken += 1;
        return context;
    }

    private async ensureContextReady(): Promise<AudioContext | null> {
        const context = this.getContext();

        if (!context) {
            return null;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state === "running") {
            this.primeContext(context);
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return null;
        }

        return context;
    }

    private primeContext(context: AudioContext) {
        if (this.primed) {
            return;
        }

        const createBuffer = (context as AudioContext & { createBuffer?: AudioContext["createBuffer"] }).createBuffer;

        if (typeof createBuffer !== "function") {
            this.primed = true;
            return;
        }

        try {
            const buffer = createBuffer.call(context, 1, 1, Math.max(22050, context.sampleRate || 22050));
            const source = context.createBufferSource();
            const gain = context.createGain();

            gain.gain.value = 0;
            source.buffer = buffer;
            source.connect(gain);
            gain.connect(context.destination);
            source.start(0);
            source.onended = () => {
                source.disconnect();
                gain.disconnect();
            };
            this.primed = true;
        } catch {
            this.primed = true;
        }
    }

    private load(sound: VaultBreakersSound): Promise<void> {
        if (this.buffers.has(sound)) {
            return Promise.resolve();
        }

        const existingLoad = this.loading.get(sound);

        if (existingLoad) {
            return existingLoad;
        }

        const context = this.getContext();

        if (!context) {
            return Promise.resolve();
        }

        const contextToken = this.contextToken;
        let loadPromise: Promise<void>;
        loadPromise = fetch(SOUND_PATHS[sound])
            .then((response) => {
                if (!response.ok) {
                    throw new Error(`Unable to load ${sound} sound`);
                }

                return response.arrayBuffer();
            })
            .then((arrayBuffer) => {
                if (this.isContextStale(context, contextToken)) {
                    return null;
                }

                return decodeAudioDataCompat(context, arrayBuffer);
            })
            .then((buffer) => {
                if (!buffer || this.isContextStale(context, contextToken)) {
                    return;
                }

                this.buffers.set(sound, buffer);
            })
            .catch(() => undefined)
            .finally(() => {
                if (this.loading.get(sound) === loadPromise) {
                    this.loading.delete(sound);
                }
            });

        this.loading.set(sound, loadPromise);
        return loadPromise;
    }

    private play(sound: VaultBreakersSound) {
        void this.playWhenReady(sound);
    }

    private async playWhenReady(sound: VaultBreakersSound) {
        const context = await this.ensureContextReady();

        if (!context) {
            return;
        }

        if (!this.buffers.has(sound)) {
            await this.load(sound);
        }

        const buffer = this.buffers.get(sound);

        if (!buffer) {
            return;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state !== "running" || this.disposed) {
            return;
        }

        this.startBufferedSound(context, sound, buffer);
    }

    private async playQuestionThemeWhenReady(token: number, theme: VaultBreakersQuestionTheme) {
        const context = await this.ensureContextReady();
        const sound = QUESTION_THEME_SOUND[theme];

        if (!context) {
            return;
        }

        if (!this.buffers.has(sound)) {
            await this.load(sound);
        }

        const buffer = this.buffers.get(sound);

        if (!buffer) {
            return;
        }

        if (context.state !== "running") {
            await context.resume().catch(() => undefined);
        }

        if (context.state !== "running" || this.disposed || this.questionThemeToken !== token) {
            return;
        }

        this.stopQuestionTheme(0, { cancelPending: false });

        if (this.questionThemeToken !== token) {
            return;
        }

        const source = context.createBufferSource();
        const gain = context.createGain();

        source.buffer = buffer;
        source.loop = true;
        source.playbackRate.value = QUESTION_THEME_PLAYBACK_RATE[theme];
        gain.gain.value = this.getOutputVolume(sound);
        source.connect(gain);
        gain.connect(context.destination);
        this.activeQuestionTheme = { source, gain, token, theme };
        source.onended = () => {
            if (this.activeQuestionTheme?.token === token) {
                this.activeQuestionTheme = null;
            }

            source.disconnect();
            gain.disconnect();
        };
        source.start(0, QUESTION_THEME_START_OFFSET_SECONDS[theme]);
    }

    private startBufferedSound(context: AudioContext, sound: VaultBreakersSound, buffer: AudioBuffer) {
        const source = context.createBufferSource();
        const gain = context.createGain();

        source.buffer = buffer;
        gain.gain.value = this.getOutputVolume(sound);
        source.connect(gain);
        gain.connect(context.destination);
        source.onended = () => {
            source.disconnect();
            gain.disconnect();
        };
        source.start();
    }

    private stopQuestionTheme(fadeMs: number, options: { cancelPending?: boolean } = {}) {
        if (options.cancelPending) {
            this.questionThemeToken += 1;
        }

        const activeQuestionTheme = this.activeQuestionTheme;

        if (!activeQuestionTheme) {
            return;
        }

        const context = this.context;
        const fadeSeconds = Math.max(0, fadeMs) / 1000;

        this.activeQuestionTheme = null;

        if (!context || context.state === "closed" || fadeSeconds <= 0) {
            try {
                activeQuestionTheme.source.stop();
            } catch {
                // Already stopped.
            }

            return;
        }

        const now = context.currentTime;
        const stopAt = now + fadeSeconds + 0.05;

        activeQuestionTheme.gain.gain.cancelScheduledValues(now);
        activeQuestionTheme.gain.gain.setValueAtTime(
            Math.max(0.0001, activeQuestionTheme.gain.gain.value),
            now
        );
        activeQuestionTheme.gain.gain.linearRampToValueAtTime(0.0001, now + fadeSeconds);

        try {
            activeQuestionTheme.source.stop(stopAt);
        } catch {
            // Already stopped.
        }
    }

    private resetContext(context: AudioContext | null, options: { clearBuffers?: boolean } = {}) {
        if (context && this.context && context !== this.context) {
            return;
        }

        this.context = null;
        this.primed = false;
        this.loading.clear();
        this.activeQuestionTheme = null;

        if (options.clearBuffers) {
            this.buffers.clear();
        }

        this.contextToken += 1;
        this.questionThemeToken += 1;
    }

    private isContextStale(context: AudioContext, contextToken: number): boolean {
        if (this.disposed || this.context !== context || this.contextToken !== contextToken) {
            return true;
        }

        if (context.state === "closed") {
            this.resetContext(context);
            return true;
        }

        return false;
    }

    private getOutputVolume(sound: VaultBreakersSound): number {
        const baseVolume = SOUND_VOLUME[sound] * GLOBAL_SOUND_VOLUME_MULTIPLIER;

        if (!this.isPhoneSizedTouchDevice()) {
            return baseVolume;
        }

        return baseVolume * MOBILE_SOUND_VOLUME_MULTIPLIER;
    }

    private isPhoneSizedTouchDevice(): boolean {
        const shortSide = Math.min(window.innerWidth || Number.POSITIVE_INFINITY, window.innerHeight || Number.POSITIVE_INFINITY);
        const hasTouchPrimaryInput =
            navigator.maxTouchPoints > 0 ||
            window.matchMedia("(pointer: coarse)").matches ||
            window.matchMedia("(any-pointer: coarse)").matches;

        return hasTouchPrimaryInput && shortSide <= MOBILE_SOUND_SHORT_SIDE_MAX_PX;
    }
}
